import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { PrivacyNotice } from "./PrivacyNotice";
import {
  ArrowRight, ArrowLeft, Check, AlertTriangle, ChevronDown, ChevronUp,
  Send, Loader2, Cpu
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import {
  ERP_HERO, ERP_STATS, ERP_PROBLEM, ERP_MODULES, CRM_CAPABILITIES,
  INTEGRATION_BENEFITS, ERP_INDUSTRIES, ERP_REGIONAL, ERP_APPROACH,
  ERP_OUTCOMES, ERP_MODERNISE, ERP_FAQ
} from "../data/erpCrmData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Section = ({ id, bg = "bg-white", children }) => (
  <section id={id} className={`py-20 lg:py-28 ${bg}`}>
    <div className="max-w-7xl mx-auto px-6">{children}</div>
  </section>
);

/* ── Hero ── */
const ERPHero = () => (
  <section className="relative min-h-[85vh] flex flex-col justify-center bg-[#1a3a42] overflow-hidden">
    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#308795]/10 rounded-full -translate-y-1/3 translate-x-1/3" />
    <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#7EA13E]/[0.06] rounded-full translate-y-1/3 -translate-x-1/4" />

    <div className="relative max-w-7xl mx-auto px-6 pt-32 pb-16 lg:pt-40 lg:pb-24 w-full">
      <div className="flex items-center justify-between mb-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#A9B294] hover:text-[#E5BD77] transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
        <div className="inline-flex items-center gap-2 bg-[#308795]/20 text-[#80d4df] text-sm font-semibold px-4 py-1.5 rounded-full">
          <span className="w-2 h-2 bg-[#308795] rounded-full animate-pulse" />
          {ERP_HERO.eyebrow}
        </div>
      </div>

      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-5 max-w-4xl">
        {ERP_HERO.headline}
      </h1>

      <p className="text-lg lg:text-xl text-[#b0c4c9] leading-relaxed max-w-2xl mb-10">
        {ERP_HERO.subheadline}
      </p>

      <div className="flex flex-col sm:flex-row gap-4 mb-16">
        <a href={ERP_HERO.primaryCTA.href}>
          <Button className="bg-[#308795] hover:bg-[#267580] text-white font-semibold px-8 py-6 text-base rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-[#308795]/20 hover:-translate-y-0.5 group">
            {ERP_HERO.primaryCTA.label}
            <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Button>
        </a>
        <a href={ERP_HERO.secondaryCTA.href}>
          <Button variant="outline" className="border-2 border-white/20 text-white hover:border-[#E5BD77] hover:text-[#E5BD77] font-semibold px-8 py-6 text-base rounded-xl transition-all duration-300">
            {ERP_HERO.secondaryCTA.label}
          </Button>
        </a>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {ERP_STATS.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="bg-white/[0.06] backdrop-blur-sm border border-white/[0.08] rounded-xl px-5 py-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#308795]/20 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-[#80d4df]" />
              </div>
              <div>
                <div className="text-lg font-bold text-white">{s.value}</div>
                <div className="text-xs text-[#8ab0b8] font-medium">{s.label}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ── Problem ── */
const ERPProblem = () => (
  <Section bg="bg-[#FAFAF7]">
    <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
      <div>
        <span className="text-sm font-bold text-[#E95D54] uppercase tracking-widest mb-3 block">The Challenge</span>
        <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-4">{ERP_PROBLEM.headline}</h2>
        <p className="text-[#6B6567] text-lg leading-relaxed">{ERP_PROBLEM.description}</p>
      </div>
      <div className="mt-10 lg:mt-0">
        <div className="bg-white rounded-2xl border border-[#E0D1BB]/30 p-8">
          <h3 className="text-sm font-bold text-[#308795] uppercase tracking-widest mb-5">The Cost of Disconnection</h3>
          <div className="space-y-3">
            {ERP_PROBLEM.symptoms.map((s, i) => (
              <div key={i} className="flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 mt-0.5 text-[#E5BD77] shrink-0" />
                <span className="text-sm text-[#494546]">{s}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </Section>
);

/* ── ERP Modules ── */
const ERPModules = () => (
  <Section id="erp-modules" bg="bg-white">
    <div className="max-w-3xl mb-14">
      <span className="text-sm font-bold text-[#308795] uppercase tracking-widest mb-3 block">ERP Platform</span>
      <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-3">The Operating System of Your Business</h2>
      <p className="text-[#6B6567] text-lg leading-relaxed">Your ERP should not just record transactions. It should drive performance. We implement Microsoft Dynamics Business Central solutions that integrate every function.</p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {ERP_MODULES.map((m, i) => {
        const Icon = m.icon;
        return (
          <div key={i} className="group bg-[#FAFAF7] border border-[#E0D1BB]/30 rounded-2xl p-6 transition-all duration-300 hover:shadow-md hover:border-[#308795]/20 hover:-translate-y-0.5">
            <div className="w-12 h-12 rounded-xl bg-[#308795]/[0.08] flex items-center justify-center mb-4">
              <Icon className="w-6 h-6 text-[#308795]" />
            </div>
            <h3 className="text-base font-bold text-[#2C2A2B] mb-1.5">{m.title}</h3>
            <p className="text-xs text-[#6B6567] leading-relaxed">{m.desc}</p>
          </div>
        );
      })}
    </div>
  </Section>
);

/* ── CRM ── */
const CRMSection = () => (
  <Section bg="bg-[#FAFAF7]">
    <div className="max-w-3xl mb-14">
      <span className="text-sm font-bold text-[#7EA13E] uppercase tracking-widest mb-3 block">CRM Platform</span>
      <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-3">Turning Customer Data into Revenue Intelligence</h2>
      <p className="text-[#6B6567] text-lg leading-relaxed">Customer relationships are assets. Without structured CRM, opportunities slip through cracks. We align CRM with billing and ERP — so front-end sales connects directly to financial performance.</p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {CRM_CAPABILITIES.map((c, i) => {
        const Icon = c.icon;
        return (
          <div key={i} className="group bg-white border border-[#E0D1BB]/30 rounded-2xl p-6 transition-all duration-300 hover:shadow-md hover:border-[#7EA13E]/20 hover:-translate-y-0.5">
            <div className="w-12 h-12 rounded-xl bg-[#7EA13E]/[0.08] flex items-center justify-center mb-4">
              <Icon className="w-6 h-6 text-[#7EA13E]" />
            </div>
            <h3 className="text-base font-bold text-[#2C2A2B] mb-1.5">{c.title}</h3>
            <p className="text-xs text-[#6B6567] leading-relaxed">{c.desc}</p>
          </div>
        );
      })}
    </div>
  </Section>
);

/* ── Integration Multiplier ── */
const IntegrationSection = () => (
  <Section bg="bg-[#465155]">
    <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
      <div>
        <span className="text-sm font-bold text-[#E5BD77] uppercase tracking-widest mb-3 block">The Multiplier Effect</span>
        <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight mb-4">Integrated ERP + CRM</h2>
        <p className="text-[#D5CFC9] text-lg leading-relaxed mb-6">This integration is where SMEs gain unfair advantage. Not because they are bigger. But because they are smarter.</p>
        <p className="text-[#E5BD77] font-semibold italic">"Sales knows what finance sees. Management sees what operations deliver. That's alignment."</p>
      </div>
      <div className="mt-10 lg:mt-0 space-y-3">
        {INTEGRATION_BENEFITS.map((b, i) => (
          <div key={i} className="flex items-start gap-4 bg-white/[0.06] border border-white/[0.08] rounded-xl px-5 py-4">
            <Check className="w-5 h-5 text-[#7EA13E] mt-0.5 shrink-0" />
            <span className="text-sm text-[#D5CFC9]">{b}</span>
          </div>
        ))}
      </div>
    </div>
  </Section>
);

/* ── Industries ── */
const IndustrySection = () => (
  <Section bg="bg-white">
    <div className="max-w-3xl mb-14">
      <span className="text-sm font-bold text-[#E95D54] uppercase tracking-widest mb-3 block">Industry Expertise</span>
      <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-3">Industry-Specific Strength</h2>
      <p className="text-[#6B6567] text-lg">We don't implement generic templates blindly. We design solutions aligned to operational realities.</p>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
      {ERP_INDUSTRIES.map((ind, i) => {
        const Icon = ind.icon;
        return (
          <div key={i} className="bg-[#FAFAF7] border border-[#E0D1BB]/30 rounded-xl p-5 text-center transition-all duration-300 hover:shadow-md hover:border-[#308795]/20">
            <div className="w-12 h-12 rounded-xl bg-[#308795]/[0.08] flex items-center justify-center mx-auto mb-3">
              <Icon className="w-6 h-6 text-[#308795]" />
            </div>
            <p className="text-sm font-semibold text-[#2C2A2B]">{ind.name}</p>
          </div>
        );
      })}
    </div>
  </Section>
);

/* ── Regional + Approach ── */
const ApproachSection = () => (
  <Section bg="bg-[#FAFAF7]">
    <div className="lg:grid lg:grid-cols-2 lg:gap-16">
      {/* Regional */}
      <div className="mb-12 lg:mb-0">
        <span className="text-sm font-bold text-[#308795] uppercase tracking-widest mb-3 block">Regional Growth</span>
        <h2 className="text-2xl lg:text-3xl font-bold text-[#2C2A2B] leading-tight mb-3">Built for ASEAN Expansion</h2>
        <p className="text-[#6B6567] text-base leading-relaxed mb-6">Growth should not require replacing systems every three years. We build ERP architectures that support expansion across Singapore, Malaysia, Indonesia, Vietnam, Philippines, and broader ASEAN.</p>
        <div className="space-y-3">
          {ERP_REGIONAL.map((r, i) => (
            <div key={i} className="flex items-center gap-3 bg-white border border-[#E0D1BB]/30 rounded-xl px-5 py-3">
              <Check className="w-4 h-4 text-[#308795] shrink-0" />
              <span className="text-sm font-medium text-[#494546]">{r}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Approach */}
      <div>
        <span className="text-sm font-bold text-[#7EA13E] uppercase tracking-widest mb-3 block">Our Approach</span>
        <h2 className="text-2xl lg:text-3xl font-bold text-[#2C2A2B] leading-tight mb-3">Digital Maturity Without Disruption</h2>
        <p className="text-[#6B6567] text-base leading-relaxed mb-6">We understand SME constraints — lean teams, limited IT bandwidth, tight budgets. We don't disappear after go-live.</p>
        <div className="space-y-3">
          {ERP_APPROACH.map((a, i) => (
            <div key={i} className="flex items-start gap-4 bg-white border border-[#E0D1BB]/30 rounded-xl px-5 py-3">
              <span className="text-xs font-bold text-[#7EA13E] bg-[#7EA13E]/10 px-2 py-1 rounded mt-0.5 shrink-0">{a.step}</span>
              <div>
                <p className="text-sm font-bold text-[#2C2A2B]">{a.title}</p>
                <p className="text-xs text-[#6B6567]">{a.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </Section>
);

/* ── Trust / Customer Logos ── */
const TrustSection = () => (
  <Section bg="bg-white">
    <div className="text-center max-w-3xl mx-auto mb-12">
      <span className="text-sm font-bold text-[#E95D54] uppercase tracking-widest mb-3 block">Trusted By</span>
      <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-4">180+ Deployments. 15 Industries. 20+ Years of Trust.</h2>
      <p className="text-[#6B6567] text-lg">Some of our ERP (Business Central) customers have been served for more than 20 years. Integrity is how we retain clients for 10–20 years.</p>
    </div>
    <div className="bg-[#FAFAF7] border border-[#E0D1BB]/30 rounded-2xl p-8 lg:p-12">
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-6">
        {ERP_OUTCOMES.map((o, i) => (
          <div key={i} className="text-center">
            <div className="w-12 h-12 rounded-full bg-[#308795]/[0.08] flex items-center justify-center mx-auto mb-2">
              <Check className="w-5 h-5 text-[#308795]" />
            </div>
            <p className="text-xs font-semibold text-[#2C2A2B] leading-snug">{o}</p>
          </div>
        ))}
      </div>
    </div>
  </Section>
);

/* ── FAQ ── */
const ERPFAQ = () => {
  const [open, setOpen] = useState(null);
  return (
    <Section bg="bg-[#FAFAF7]">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-sm font-bold text-[#308795] uppercase tracking-widest mb-3 block">Frequently Asked Questions</span>
          <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight">Everything You Need to Know About ERP & CRM</h2>
        </div>
        <div className="space-y-3">
          {ERP_FAQ.map((item, i) => (
            <div key={i} className="border border-[#E0D1BB]/40 rounded-xl overflow-hidden bg-white transition-all duration-300">
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-[#FAFAF7] transition-colors">
                <span className="text-base font-semibold text-[#2C2A2B] pr-4">{item.q}</span>
                {open === i ? <ChevronUp className="w-5 h-5 text-[#308795] shrink-0" /> : <ChevronDown className="w-5 h-5 text-[#D5CFC9] shrink-0" />}
              </button>
              {open === i && (
                <div className="px-6 pb-5 animate-fade-in">
                  <p className="text-sm text-[#6B6567] leading-relaxed">{item.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};

/* ── CTA Contact ── */
const ERPContact = () => {
  const [formData, setFormData] = useState({ firstName: "", lastName: "", email: "", company: "", website: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email) { toast.error("Please fill in your first name, last name, and email."); return; }
    setLoading(true);
    try {
      const mp=[]; if(formData.company) mp.push(`Company: ${formData.company}`); if(formData.website) mp.push(`Website: ${formData.website}`); if(formData.message) mp.push(formData.message);
      await axios.post(`${API}/enquiries`, { name: `${formData.firstName} ${formData.lastName}`, email: formData.email, message: mp.join("\n") || null, selected_path: "ERP & CRM Solutions" });
      setSubmitted(true);
      toast.success("Thank you! We'll be in touch shortly.");
    } catch { toast.error("Something went wrong. Please try again."); } finally { setLoading(false); }
  };

  return (
    <Section id="erp-contact" bg="bg-[#1a3a42]">
      <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
        <div>
          <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight mb-4">Ready to Modernise Your Business Core?</h2>
          <p className="text-[#b0c4c9] text-lg leading-relaxed mb-6">Asterisk can architect the right digital backbone for your next phase of growth.</p>
          <div className="mt-6">
            <h3 className="text-sm font-bold text-white/70 uppercase tracking-wider mb-4">Whether You Are</h3>
            <div className="space-y-2">
              {ERP_MODERNISE.map((m, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-[#7EA13E] mt-0.5 shrink-0" />
                  <span className="text-sm text-[#b0c4c9]">{m}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-10 lg:mt-0">
          {!submitted ? (
            <div className="bg-white/[0.06] backdrop-blur-sm border border-white/[0.1] rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-6">Book an ERP Consultation</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#b0c4c9] mb-1.5">First Name & Last Name</label>
                  <div className="grid grid-cols-2 gap-3"><input type="text" value={formData.firstName} onChange={(e) => setFormData({...formData, firstName: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all" placeholder="First name" /><input type="text" value={formData.lastName} onChange={(e) => setFormData({...formData, lastName: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all" placeholder="Last name" /></div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#b0c4c9] mb-1.5">Email</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all" placeholder="your@email.com" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-[#b0c4c9] mb-1.5">Name of Company</label><input type="text" value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all" placeholder="Company name" /></div>
                  <div><label className="block text-sm font-medium text-[#b0c4c9] mb-1.5">Website</label><input type="url" value={formData.website} onChange={(e) => setFormData({...formData, website: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all" placeholder="https://www.example.com" /></div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#b0c4c9] mb-1.5">Message (Optional)</label>
                  <textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} rows={3} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all resize-none" placeholder="Tell us about your needs..." />
                </div>
                <PrivacyNotice checked={privacyConsent} onChange={setPrivacyConsent} dark />
                <Button type="submit" disabled={loading || !privacyConsent} className="w-full bg-[#308795] hover:bg-[#267580] text-white font-semibold py-6 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-[#308795]/20 text-base disabled:opacity-60">
                  {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</>) : (<>Get Started<Send className="ml-2 h-4 w-4" /></>)}
                </Button>
              </form>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-[#7EA13E]/20 flex items-center justify-center mx-auto mb-5">
                <svg className="w-8 h-8 text-[#7EA13E]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Thank You!</h3>
              <p className="text-[#b0c4c9]">We'll be in touch to discuss your ERP & CRM needs.</p>
            </div>
          )}
        </div>
      </div>
    </Section>
  );
};

export default function ErpCrmPage() {
  return (
    <>
      <ERPHero />
      <ERPProblem />
      <ERPModules />
      <CRMSection />
      <IntegrationSection />
      <IndustrySection />
      <ApproachSection />
      <TrustSection />
      <ERPFAQ />
      <ERPContact />
    </>
  );
}
